import React, { useEffect, useState, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { InlineSpinner } from "@/components/common/Loading";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, AlertTriangle, ImageUp, Image as ImageIcon } from "lucide-react";

import { OrgSupportRoster } from "@/components/support/OrgSupportRoster";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const EMPTY = {
  display_name: "", credentials: "", display_title: "", role_summary: "",
  how_i_can_help: [], specialties: [], categories: [], district_ids: [],
  phone: "", email: "", phone_visible: true, email_visible: false,
  message_enabled: true, schedule_enabled: true, candidate_visible: true,
  is_leadership: false, active: true, display_order: 999,
};

function linesToArr(s) { return s.split("\n").map((x) => x.trim()).filter(Boolean); }

export default function SupportTeamEditor() {
  const [meta, setMeta] = useState(null);
  const [members, setMembers] = useState(null);
  const [discrepancies, setDiscrepancies] = useState([]);
  const [editing, setEditing] = useState(null); // member object or EMPTY (new)
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const load = () => {
    api.get("/team/admin/members").then((r) => { setMembers(r.data.items); setDiscrepancies(r.data.discrepancies || []); })
      .catch((e) => { toast.error(apiError(e)); setMembers([]); });
  };
  useEffect(() => { api.get("/team/admin/meta").then((r) => setMeta(r.data)).catch((e) => toast.error(apiError(e))); load(); }, []);

  const openEdit = (m) => {
    setEditing(m || {});
    setForm(m ? {
      ...EMPTY, ...m,
      how_i_can_help: m.how_i_can_help || [], specialties: m.specialties || [],
      categories: m.categories || [], district_ids: m.district_ids || [],
      phone: m.phone_raw || m.phone || "", email: m.email_raw || m.email || "",
    } : { ...EMPTY, district_ids: !meta.can_manage_all && meta.districts.length === 1 ? [meta.districts[0].id] : [] });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (editing && editing.id) {
        await api.patch(`/team/admin/members/${editing.id}`, payload);
        toast.success("Team member updated");
      } else {
        await api.post("/team/admin/members", payload);
        toast.success("Team member added");
      }
      setEditing(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name} from the support team?`)) return;
    try { await api.delete(`/team/admin/members/${m.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const uploadPhoto = async (m, file) => {
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/team/admin/members/${m.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Photo updated"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const removePhoto = async (m) => { try { await api.delete(`/team/admin/members/${m.id}/photo`); load(); } catch (e) { toast.error(apiError(e)); } };
  const assignSourcePhoto = async (m, key) => { try { await api.post(`/team/admin/members/${m.id}/photo-from-source`, { storage_key: key }); toast.success("Photo set"); load(); } catch (e) { toast.error(apiError(e)); } };

  if (!meta || members === null) return <div className="py-16 flex justify-center"><InlineSpinner /></div>;
  const needsDistrict = !meta.can_manage_all && form.district_ids.length === 0;
  const districtName = (id) => (meta.districts.find((d) => d.id === id) || {}).name || id;

  return (
    <div className="max-w-5xl" data-testid="support-team-editor">
      <OrgSupportRoster />
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Support Team</h1>
          <p className="text-sm text-slate-500 mt-1">Manage the candidate-facing "My Team" directory. Editing a profile never changes the person's User account.</p>
        </div>
        <Button onClick={() => openEdit(null)} data-testid="team-add-member" className="bg-[#0D9488] hover:bg-[#0b7d73]">
          <Plus className="w-4 h-4 mr-1.5" />Add Member
        </Button>
      </div>

      {discrepancies.length > 0 && (
        <div className="mb-5 rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="team-discrepancies">
          <p className="font-semibold text-amber-800 flex items-center gap-2 text-sm"><AlertTriangle className="w-4 h-4" />Needs Review — contact information</p>
          <ul className="mt-2 space-y-1">
            {discrepancies.map((d) => <li key={d.id} className="text-sm text-amber-800"><strong>{d.name}:</strong> {d.reason}</li>)}
          </ul>
        </div>
      )}

      <div className="space-y-3">
        {members.map((m) => (
          <div key={m.id} className="rounded-xl border border-slate-200 bg-white p-4 flex items-center gap-4" data-testid={`team-row-${m.id}`}>
            {m.photo_url ? <img src={`${BACKEND}${m.photo_url}`} alt="" className="w-12 h-12 rounded-xl object-cover bg-slate-100" />
              : <span className="w-12 h-12 rounded-xl bg-slate-100 text-slate-400 flex items-center justify-center"><ImageIcon className="w-5 h-5" /></span>}
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-slate-900 flex items-center gap-2 flex-wrap">{m.name}
                {!m.active && <Badge variant="outline" className="text-[10px]">Inactive</Badge>}
                {m.is_leadership && <Badge className="bg-[#013A5A] text-white border-0 text-[10px]">Leadership</Badge>}
                {!m.candidate_visible && <Badge variant="outline" className="text-[10px] border-slate-300">Hidden</Badge>}
                {m.contact_review && <Badge className="bg-amber-100 text-amber-800 border-0 text-[10px]">Review</Badge>}
              </div>
              <p className="text-xs text-slate-500">{m.title} · {(m.district_ids || []).map(districtName).join(", ") || "All districts"}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">{(m.categories || []).join(" · ")}{m.has_schedule_page ? " · Scheduling ✓" : ""}</p>
            </div>
            <div className="flex items-center gap-1.5">
              <Button size="sm" variant="outline" onClick={() => openEdit(m)} data-testid={`team-edit-${m.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
              <Button size="sm" variant="outline" onClick={() => remove(m)} data-testid={`team-delete-${m.id}`} className="text-rose-600 hover:text-rose-700"><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        ))}
        {members.length === 0 && <p className="text-sm text-slate-400">No team members yet.</p>}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="team-edit-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit Team Member" : "Add Team Member"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {editing?.id && (
              <div className="flex items-center gap-3 rounded-lg border border-slate-200 p-3">
                {editing.photo_url ? <img src={`${BACKEND}${editing.photo_url}`} alt="" className="w-14 h-14 rounded-xl object-cover" />
                  : <span className="w-14 h-14 rounded-xl bg-slate-100 text-slate-400 flex items-center justify-center"><ImageIcon className="w-5 h-5" /></span>}
                <div className="flex-1">
                  <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => e.target.files[0] && uploadPhoto(editing, e.target.files[0])} />
                  <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="team-photo-upload"><ImageUp className="w-3.5 h-3.5 mr-1.5" />Upload Photo</Button>
                  {editing.has_photo && <Button size="sm" variant="ghost" onClick={() => removePhoto(editing)} className="text-rose-600 ml-2">Remove</Button>}
                  {meta.source_photos?.length > 0 && (
                    <div className="mt-2">
                      <p className="text-[11px] text-slate-400 mb-1">Source chart photos:</p>
                      <div className="flex flex-wrap gap-1.5">
                        {meta.source_photos.map((p) => (
                          <button key={p.storage_key} onClick={() => assignSourcePhoto(editing, p.storage_key)} title={p.label}
                            className="w-9 h-9 rounded-md bg-[#006090]/10 text-[#006090] text-[10px] font-semibold flex items-center justify-center hover:bg-[#006090]/20">
                            {p.label.replace("Source photo ", "#")}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name</Label><Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} data-testid="team-f-name" /></div>
              <div><Label>Credentials</Label><Input value={form.credentials || ""} onChange={(e) => setForm({ ...form, credentials: e.target.value })} placeholder="CLU" /></div>
            </div>
            <div><Label>Title</Label><Input value={form.display_title} onChange={(e) => setForm({ ...form, display_title: e.target.value })} data-testid="team-f-title" /></div>
            <div><Label>Role summary</Label><Textarea value={form.role_summary || ""} onChange={(e) => setForm({ ...form, role_summary: e.target.value })} rows={2} /></div>
            <div><Label>How I can help (one per line)</Label><Textarea value={(form.how_i_can_help || []).join("\n")} onChange={(e) => setForm({ ...form, how_i_can_help: linesToArr(e.target.value) })} rows={3} /></div>
            <div><Label>Specialties (one per line)</Label><Textarea value={(form.specialties || []).join("\n")} onChange={(e) => setForm({ ...form, specialties: linesToArr(e.target.value) })} rows={3} /></div>
            <div>
              <Label>Categories</Label>
              <div className="grid grid-cols-2 gap-1.5 mt-1">
                {meta.categories.map((c) => (
                  <label key={c.key} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={form.categories.includes(c.key)} onCheckedChange={(v) => setForm({ ...form, categories: v ? [...form.categories, c.key] : form.categories.filter((x) => x !== c.key) })} />
                    {c.label}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label>Districts</Label>
              <div className="flex flex-wrap gap-3 mt-1">
                {meta.districts.map((d) => (
                  <label key={d.id} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={form.district_ids.includes(d.id)} onCheckedChange={(v) => setForm({ ...form, district_ids: v ? [...form.district_ids, d.id] : form.district_ids.filter((x) => x !== d.id) })} />
                    {d.name}
                  </label>
                ))}
              </div>
              <p className={`text-[11px] mt-1 ${needsDistrict ? "text-amber-700" : "text-slate-400"}`} data-testid="team-district-hint">
                {meta.can_manage_all ? "Leave all unchecked = shown to all districts." : "Select at least one of your districts."}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Phone</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><Label>Display order</Label><Input type="number" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: Number(e.target.value) })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              {[["phone_visible", "Show phone"], ["email_visible", "Show email"], ["message_enabled", "Allow message"],
                ["schedule_enabled", "Allow scheduling"], ["candidate_visible", "Visible to candidates"],
                ["is_leadership", "District leadership"], ["active", "Active"]].map(([k, lbl]) => (
                <label key={k} className="flex items-center justify-between gap-2 text-sm">
                  {lbl}<Switch checked={!!form[k]} onCheckedChange={(v) => setForm({ ...form, [k]: v })} data-testid={`team-f-${k}`} />
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.display_name || !form.display_title || needsDistrict} data-testid="team-save" className="bg-[#0D9488] hover:bg-[#0b7d73]">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
